import { ALLIANCE_CONTRIBUTION_POINTS_PER_POPULATION, ALLIANCE_MAX_MEMBERS, ALLIANCE_BUILDING, ALLIANCE_REQUIRED_BUILDING_LEVEL, ALLIANCE_ROLE_PERMISSIONS, ALLIANCE_ROLE_RANK, BUILDING_NAMES, PLAYER_POINTS_DIVISOR, SUN_CONTROL_MEMBERSHIP_FREEZE_COUNT, } from "@kaeliux/shared";
import { calculateDockedPopulation } from "./ships.js";
/**
 * Pure alliance rules: permission checks, the founding prerequisite, the
 * membership freeze, and alliance points and contributions. Every gameplay
 * decision the alliance
 * endpoints make routes through one of these — `apps/functions/src/
 * alliances.ts` only ever does I/O and transaction bookkeeping around them.
 *
 * Nothing here touches Firestore, `Date.now()`, or randomness: timestamps
 * arrive as parameters, same as the rest of this package.
 */
// ---------------------------------------------------------------------------
// Permissions
// ---------------------------------------------------------------------------
/** Whether `role` carries `permission` — see `ALLIANCE_ROLE_PERMISSIONS`. */
export function hasAlliancePermission(role, permission) {
    if (!role)
        return false;
    return ALLIANCE_ROLE_PERMISSIONS[role].includes(permission);
}
/**
 * Whether `actorRole` outranks `targetRole` strictly. Kicking, promoting,
 * and demoting all share this rule: you may only act on someone below you,
 * never a peer and never yourself-by-rank. An officer can therefore kick a
 * member but not another officer, and nobody can act on the leader.
 */
export function outranks(actorRole, targetRole) {
    return ALLIANCE_ROLE_RANK[actorRole] > ALLIANCE_ROLE_RANK[targetRole];
}
const ALLOWED = { allowed: true };
function denied(reason) {
    return { allowed: false, reason };
}
/**
 * Whether `actor` may kick / promote / demote `target`. `actorId` and
 * `targetId` are compared so self-targeting is rejected with a clearer
 * message than the rank rule would give (a leader outranks every member but
 * must still not be able to kick themselves — leaving is `/leaveAlliance`,
 * which has its own last-leader handling).
 */
export function canManageMember(actor, target, permission) {
    if (actor.userId === target.userId)
        return denied("You cannot perform this action on yourself");
    if (!hasAlliancePermission(actor.role, permission))
        return denied("Your alliance role does not allow this");
    if (!outranks(actor.role, target.role))
        return denied("You cannot act on a member of equal or higher rank");
    return ALLOWED;
}
/**
 * Whether a player may found an alliance. `buildings` is the planet's
 * `buildings` map; `planets` is every planet the player owns, since the
 * Team building only has to exist on ONE of them (a player with a
 * Command Center on a colony but not the homeworld still qualifies).
 */
export function canCreateAlliance(planets, currentAllianceId) {
    if (currentAllianceId)
        return denied("Leave your current alliance before founding a new one");
    const level = highestAllianceBuildingLevel(planets);
    if (level < ALLIANCE_REQUIRED_BUILDING_LEVEL) {
        return denied(`Founding an alliance requires the ${BUILDING_NAMES[ALLIANCE_BUILDING]} at Level ${ALLIANCE_REQUIRED_BUILDING_LEVEL} on one of your planets`);
    }
    return ALLOWED;
}
/** Highest {@link ALLIANCE_BUILDING} level across every owned planet; 0 when none has it. */
export function highestAllianceBuildingLevel(planets) {
    let best = 0;
    for (const planet of planets) {
        const level = planet.buildings?.[ALLIANCE_BUILDING]?.level ?? 0;
        if (level > best)
            best = level;
    }
    return best;
}
/**
 * Whether an applicant may be accepted into an alliance of `memberCount`
 * members. Checked at ACCEPT time rather than at apply time: applications
 * outlive the moment they were filed, and a full alliance that later loses a
 * member should be able to accept a queued applicant without them
 * re-applying.
 */
export function canAcceptMember(memberCount) {
    if (memberCount >= ALLIANCE_MAX_MEMBERS) {
        return denied(`This alliance is full (${ALLIANCE_MAX_MEMBERS} members)`);
    }
    return ALLOWED;
}
/** Shown when joining is refused by the membership freeze — deliberately names no alliance and no count. */
export const ALLIANCE_MEMBERSHIP_FROZEN_MESSAGE = "Alliance membership is frozen for the rest of this round";
/**
 * Whether alliance membership may change at all right now. Once any alliance
 * controls `SUN_CONTROL_MEMBERSHIP_FREEZE_COUNT` Suns, membership freezes
 * altogether for every alliance in the universe for the rest of the round:
 * nobody applies, is accepted, founds, leaves or is kicked, and no alliance
 * disbands.
 */
export function canChangeAllianceMembership(membershipFrozenAt) {
    return membershipFrozenAt ? denied(ALLIANCE_MEMBERSHIP_FROZEN_MESSAGE) : ALLOWED;
}
/**
 * Who leadership auto-transfers to when a leader leaves an alliance that
 * still has other members, rather than being refused or requiring a manual
 * transfer first (2026-08-15 ruling — see `apps/functions/src/alliances.ts`'s
 * `leaveAlliance`): the remaining member with the highest ranking total.
 * Ties break by earliest `joinedAt`, then `userId`, so the result is
 * deterministic across a transaction retry on the exact same input. The
 * caller only ever invokes this when at least one other member exists.
 */
export function selectAutoTransferLeader(candidates) {
    return candidates.reduce((best, candidate) => {
        if (candidate.totalPoints !== best.totalPoints)
            return candidate.totalPoints > best.totalPoints ? candidate : best;
        if (candidate.joinedAt.getTime() !== best.joinedAt.getTime()) {
            return candidate.joinedAt.getTime() < best.joinedAt.getTime() ? candidate : best;
        }
        return candidate.userId < best.userId ? candidate : best;
    });
}
/**
 * Case-folded uniqueness key for an alliance name. Firestore has no
 * case-insensitive index, so the fold is stored as its own field
 * (`Alliance.nameKey`) and queried on equality. Interior whitespace is
 * collapsed too, so "Red  Star" and "Red Star" collide rather than
 * coexisting as visually identical alliances.
 *
 * Deliberately `toLowerCase`, not `toLocaleLowerCase`: this key is PERSISTED
 * and compared across machines, and the locale-aware fold is not
 * environment-independent — under a Turkish locale `"ISTANBUL"` folds to
 * `"ıstanbul"` rather than `"istanbul"`, so a browser (the mock service) and
 * the server would disagree about whether two names collide. Same reasoning
 * for {@link allianceTagKey} and {@link normalizeAllianceTag}, and it is what
 * this package's determinism rule requires.
 */
export function allianceNameKey(name) {
    return name.trim().replace(/\s+/g, " ").toLowerCase();
}
/** Case-folded uniqueness key for a tag. Tags carry no whitespace (see `ALLIANCE_TAG_PATTERN`). */
export function allianceTagKey(tag) {
    return tag.trim().toLowerCase();
}
/** Display form of a tag — always uppercase, so `[spqr]` and `[SPQR]` can't both exist visually. */
export function normalizeAllianceTag(tag) {
    return tag.trim().toUpperCase();
}
// ---------------------------------------------------------------------------
// Alliance points and contributions
// ---------------------------------------------------------------------------
/** Resource points for what an alliance spent on its Team planet, at the player-points rate. */
export function allianceResourcePoints(resourcesSpent) {
    return Math.max(0, Math.floor(resourcesSpent / PLAYER_POINTS_DIVISOR));
}
/** Total alliance points — resource points plus battle points — the third victory tiebreak. */
export function allianceTotalPoints(battlePoints, resourcesSpent) {
    return battlePoints + allianceResourcePoints(resourcesSpent);
}
/**
 * Metal, minerals and gas that left a Team planet's stock between two
 * snapshots of the same moment — what a queue action spent (negative for a
 * cancellation's refund). Energy is never spent this way.
 */
export function economicResourcesSpent(before, after) {
    return (before.resources.metal.current - after.resources.metal.current)
        + (before.resources.minerals.current - after.resources.minerals.current)
        + (before.resources.gas.current - after.resources.gas.current);
}
/**
 * What a member's flight that reached the Team planet contributes: the
 * resources it actually unloaded, plus — for Station — the population of the
 * ships it handed over. Every other mission contributes nothing.
 */
export function contributionFromTeamPlanetDelivery(mission, race, delivered, shipsTransferred) {
    if (mission !== "transport" && mission !== "station")
        return { resources: 0, population: 0 };
    const resources = delivered ? delivered.metal + delivered.minerals + delivered.gas + delivered.scrap : 0;
    const population = mission === "station" ? calculateDockedPopulation(race, shipsTransferred) : 0;
    return { resources, population };
}
/** Contribution points from a member's running totals. */
export function allianceContributionPoints(contribution) {
    return Math.floor(contribution.resources / PLAYER_POINTS_DIVISOR)
        + contribution.population * ALLIANCE_CONTRIBUTION_POINTS_PER_POPULATION;
}
/**
 * The Sun count an alliance shows one viewer. Its own members always see the
 * number; so does everyone while its Suns aren't concealed. A concealed
 * alliance holding the freeze count or more reads "5 or more" instead.
 */
export function visibleAllianceSunCount(count, concealed, viewerIsMember) {
    if (viewerIsMember || !concealed || count < SUN_CONTROL_MEMBERSHIP_FREEZE_COUNT) {
        return { sunCount: count, sunCountIsMinimum: false };
    }
    return { sunCount: SUN_CONTROL_MEMBERSHIP_FREEZE_COUNT, sunCountIsMinimum: true };
}
/**
 * Case-insensitive substring match against an alliance's name and tag —
 * the filter behind `/listAlliances`'s `search`. An empty/whitespace query
 * matches everything, so the browse list works unfiltered.
 */
export function matchesAllianceSearch(alliance, search) {
    const needle = search.trim().toLowerCase();
    if (needle.length === 0)
        return true;
    return (alliance.name.toLowerCase().includes(needle) ||
        alliance.tag.toLowerCase().includes(needle));
}
//# sourceMappingURL=alliance.js.map